import { useAuth0 } from "@auth0/auth0-react";
import { QueryKey, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { getHeaders } from "../../helpers/helper";
import { getUserStoreIdFromLocalStorage } from "../../helpers/localStorageHelper";
import { OrderListDeliverySetting, OrderListResponse, OrderListRow, OrderListSettingsResponse, OrderListSavedDelivery, UpdateOrderListStatusRequest } from "../../models/orderListModels";
import axiosInstance from "../config/axiosInstance";
import { QueryKeys } from "../react-query/queryKeys";
import { OrderListDeliveryRequest } from "../../helpers/orderListDateHelper";

export const useOrderListSettings = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  return useQuery<OrderListSettingsResponse>({
    queryKey: [QueryKeys.ORDER_LIST_SETTINGS, storeId],
    queryFn: async () => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.get<OrderListSettingsResponse>("/OrderListSettings", { headers });
      return data;
    },
    enabled: !!getAccessTokenSilently && !!storeId,
    refetchOnWindowFocus: true,
  });
};

export const useSaveOrderListSettings = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  const queryClient = useQueryClient();
  return useMutation<OrderListSettingsResponse, unknown, { settings: OrderListDeliverySetting[]; deletedSettingIds: string[] }>({
    mutationFn: async ({ settings, deletedSettingIds }) => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.post<OrderListSettingsResponse>("/OrderListSettings", { settings, deletedSettingIds }, { headers });
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.ORDER_LIST_SETTINGS, storeId] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.ORDER_LIST, storeId] });
    },
  });
};
export const useStoreBufferPreference = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();

  return useQuery<number>({
    queryKey: ["store-buffer-preference", storeId],
    queryFn: async () => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.get<{ bufferPercent: number }>("/StoreBufferPreference", { headers });
      return Number.isFinite(data?.bufferPercent) ? data.bufferPercent : 0;
    },
    enabled: !!getAccessTokenSilently && !!storeId,
    retry: 0,
    refetchOnWindowFocus: false,
  });
};

export const useSaveStoreBufferPreference = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  const queryClient = useQueryClient();

  return useMutation<number, unknown, number>({
    mutationFn: async (bufferPercent) => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.post<{ bufferPercent: number }>("/StoreBufferPreference", { bufferPercent }, { headers });
      return Number.isFinite(data?.bufferPercent) ? data.bufferPercent : 0;
    },
    onSuccess: async (savedBuffer) => {
      if (!storeId) {
        return;
      }

      await queryClient.setQueryData(["store-buffer-preference", storeId], savedBuffer);
      await queryClient.invalidateQueries({ queryKey: ["store-buffer-preference", storeId] });
    },
  });
};

export const useOrderListSections = (
  requests: OrderListDeliveryRequest<OrderListDeliverySetting>[],
  buffer: number,
  forceRefresh = false,
  storeToday = "",
  enabled = true
) => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  return useQueries({
    queries: requests.map(({ setting, deliveryDate }) => ({
      queryKey: [QueryKeys.ORDER_LIST, storeId, setting.id, deliveryDate, buffer, forceRefresh, storeToday],
      queryFn: async () => {
        const headers = await getHeaders(getAccessTokenSilently);
        const { data } = await axiosInstance.get<OrderListResponse>("/OrderList", {
          headers,
          params: {
            deliverySettingId: setting.id,
            deliveryDate,
            overallBufferPercent: buffer,
            forceRefresh,
          },
        });
        return data;
      },
      enabled: enabled && !!getAccessTokenSilently && !!storeId && !!deliveryDate,
      retry: 0,
      refetchOnWindowFocus: false,
    })),
  });
};

export const useOrderListHistory = (deliveryDate: string, enabled: boolean) => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  return useQuery<OrderListSavedDelivery[]>({
    queryKey: [QueryKeys.ORDER_LIST, storeId, "history", deliveryDate],
    queryFn: async () => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.get<OrderListSavedDelivery[]>("/OrderList/history", {
        headers, params: { fromDate: deliveryDate, toDate: deliveryDate },
      });
      return data;
    },
    enabled: enabled && !!getAccessTokenSilently && !!storeId && !!deliveryDate,
    retry: 0,
    refetchOnWindowFocus: true,
  });
};

const normalizeKey = (value: string | null | undefined) => (value ?? "").trim().toLowerCase();

const isSameRow = (row: OrderListRow, ingredientKey: string, unit: string) =>
  normalizeKey(row.ingredientKey) === normalizeKey(ingredientKey) && normalizeKey(row.unit) === normalizeKey(unit);

const updateRow = (
  data: OrderListResponse | undefined,
  ingredientKey: string,
  unit: string,
  update: (row: OrderListRow) => OrderListRow
): OrderListResponse | undefined => {
  if (!data?.rows?.some((row) => isSameRow(row, ingredientKey, unit))) {
    return data;
  }

  return {
    ...data,
    rows: data.rows.map((row) => (isSameRow(row, ingredientKey, unit) ? update(row) : row)),
  };
};

const pickStatusFields = (row: OrderListRow): Partial<OrderListRow> => ({
  orderStatus: row.orderStatus,
  statusVersion: row.statusVersion,
  confirmedOrderQuantity: row.confirmedOrderQuantity,
  orderQuantityChanged: row.orderQuantityChanged,
});

type StatusMutationContext = {
  previousRows: Array<[QueryKey, OrderListRow | undefined]>;
};

export const useUpdateOrderListStatus = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  const client = useQueryClient();
  const sectionKey = (request: UpdateOrderListStatusRequest) =>
    [QueryKeys.ORDER_LIST, storeId, request.deliverySettingId, request.deliveryDate];

  return useMutation<OrderListResponse, unknown, UpdateOrderListStatusRequest, StatusMutationContext>({
    mutationFn: async (request) => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.patch<OrderListResponse>("/OrderList/status", request, { headers });
      return data;
    },
    // Update only the affected row in the cache so the UI responds instantly and other rows'
    // in-flight optimistic changes are not overwritten.
    onMutate: async (request) => {
      const queryKey = sectionKey(request);
      await client.cancelQueries({ queryKey });
      const previousRows = client
        .getQueriesData<OrderListResponse>({ queryKey })
        .map(([key, data]): [QueryKey, OrderListRow | undefined] => [
          key,
          data?.rows?.find((row) => isSameRow(row, request.ingredientKey, request.unit)),
        ]);

      client.setQueriesData<OrderListResponse>({ queryKey }, (data) =>
        updateRow(data, request.ingredientKey, request.unit, (row) => ({
          ...row,
          orderStatus: request.action === "MarkNotOrdered" ? "NotOrdered" : "Ordered",
          ...(request.action === "ConfirmQuantity"
            ? { orderQuantityChanged: false, confirmedOrderQuantity: request.expectedOrderQuantity }
            : {}),
        }))
      );

      return { previousRows };
    },
    onSuccess: (data, request) => {
      const saved = data?.rows?.find((row) => isSameRow(row, request.ingredientKey, request.unit));
      if (!saved) {
        return;
      }

      client.setQueriesData<OrderListResponse>({ queryKey: sectionKey(request) }, (current) => {
        const updated = updateRow(current, request.ingredientKey, request.unit, (row) => ({
          ...row,
          orderQuantity: saved.orderQuantity,
          ...pickStatusFields(saved),
        }));
        return updated && updated !== current ? { ...updated, revision: data.revision } : updated;
      });
    },
    onError: async (_error, request, context) => {
      context?.previousRows.forEach(([key, previous]) => {
        if (!previous) {
          return;
        }
        client.setQueryData<OrderListResponse>(key, (current) =>
          updateRow(current, request.ingredientKey, request.unit, (row) => ({ ...row, ...pickStatusFields(previous) }))
        );
      });
      await client.invalidateQueries({ queryKey: sectionKey(request) });
    },
  });
};